/**
 * Fetches and caches the DP routing config (routing.json) from CloudFront.
 * Maps profile keys to DP URLs: { "production-us": "https://xk4wbn.linkedin.superblocks.com", ... }
 *
 * Returns undefined for a profile if:
 * - No dpConfigUrl is configured (feature not enabled)
 * - The fetch fails (falls back to default orchestrator)
 * - The profile has no mapping in routing.json
 * - The URL fails validation (non-HTTPS, non-superblocks.com, malformed)
 *
 * Uses stale-while-revalidate: serves cached data immediately, triggers a
 * background re-fetch when the cache is older than CACHE_TTL_MS (5 minutes).
 */

import { uiLogger } from "core/telemetry/logger";

type RoutingConfig = Record<string, string>;

export const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

let cachedConfig: RoutingConfig | null = null;
let cachedAt: number | null = null;
let fetchPromise: Promise<RoutingConfig | null> | null = null;
let revalidating = false;

async function fetchRoutingConfig(
  dpConfigUrl: string | undefined,
): Promise<RoutingConfig | null> {
  if (!dpConfigUrl) {
    return {};
  }
  try {
    const response = await fetch(dpConfigUrl, {
      cache: "no-cache",
    });
    if (!response.ok) {
      uiLogger.warn("[dp-routing] Failed to fetch routing config", {
        status: response.status,
        dpConfigUrl,
      });
      return null;
    }
    return (await response.json()) as RoutingConfig;
  } catch (error) {
    uiLogger.warn("[dp-routing] Failed to fetch routing config", {
      dpConfigUrl,
      error: String(error),
    });
    return null;
  }
}

function revalidateInBackground(dpConfigUrl: string | undefined): void {
  if (revalidating) return;
  revalidating = true;
  void fetchRoutingConfig(dpConfigUrl).then((config) => {
    if (config !== null) {
      cachedConfig = config;
    }
    // Reset TTL regardless of success/failure to avoid retrying on every call
    cachedAt = Date.now();
    revalidating = false;
  });
}

export async function getRoutingConfig(
  dpConfigUrl: string | undefined,
): Promise<RoutingConfig> {
  if (cachedConfig !== null) {
    if (cachedAt !== null && Date.now() - cachedAt >= CACHE_TTL_MS) {
      revalidateInBackground(dpConfigUrl);
    }
    return cachedConfig;
  }
  if (!fetchPromise) {
    fetchPromise = fetchRoutingConfig(dpConfigUrl).then((config) => {
      if (config !== null) {
        cachedConfig = config;
        cachedAt = Date.now();
      }
      fetchPromise = null;
      return config;
    });
  }
  return (await fetchPromise) ?? {};
}

function validateDpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") {
      uiLogger.warn("[dp-routing] Rejected non-HTTPS URL", {
        url,
      });
      return false;
    }
    if (!parsed.hostname.endsWith(".superblocks.com")) {
      uiLogger.warn("[dp-routing] Rejected URL with unexpected domain", {
        url,
      });
      return false;
    }
    return true;
  } catch {
    uiLogger.warn("[dp-routing] Rejected malformed URL", {
      url,
    });
    return false;
  }
}

export function getDpUrlForProfile(
  config: RoutingConfig,
  profileKey: string | undefined,
): string | undefined {
  if (!profileKey || !config[profileKey]) {
    return undefined;
  }
  const url = config[profileKey];
  if (!validateDpUrl(url)) {
    return undefined;
  }
  return url;
}

/** Force background revalidation on next access (e.g. after admin reassignment). */
export function invalidateRoutingConfig(): void {
  cachedAt = 0;
  revalidating = false;
  fetchPromise = null;
}

/** Full reset -- clears cached data entirely (used in tests). */
export function resetRoutingConfig(): void {
  cachedConfig = null;
  cachedAt = null;
  fetchPromise = null;
  revalidating = false;
}

export type { RoutingConfig };
